import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { buttonClasses } from "@/components/design/button-classes";
import { AgentDetail } from "@/components/match/AgentDetail";
import { agentsAdminPath } from "@/components/match/agents";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { AgentDocument } from "@/graphql/generated/operations";
import { isMissingEntityError } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";

const MAIN = "w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10";

// `/a/:id`: an AI agent's detail view, read-only, reached from global search and from every agent
// row in Match. Back returns to wherever the reader came from (BackButton, as on `/p/:id`).
// Editing stays in the Stammdaten tab, linked for those who may write there.
export function AgentPage() {
  const intl = useIntl();
  const { id = "" } = useParams();
  const canEdit = useHasPermission(Permission.WriteReferenceData);
  const { data, loading, error } = useQuery(AgentDocument, { variables: { id } });
  const agent = data?.agent ?? null;

  if (loading && !data) {
    return (
      <main className={MAIN} data-graphql="Agent">
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      </main>
    );
  }

  // Same treatment as `/c/:id`: an unknown id and a malformed one (genesis parses the segment as a
  // UUID) both say "not found"; only a genuine failure gets a support code.
  if (!agent) {
    return (
      <main className={MAIN} data-graphql="Agent">
        <div className="mb-5">
          <BackButton />
        </div>
        <NoBackend
          id="agent-page-notfound"
          label={intl.formatMessage({ id: "agent.page.notFound", defaultMessage: "AI agent not found" })}
          hint={
            error && !isMissingEntityError(error)
              ? userErrorMessage(intl, error)
              : intl.formatMessage({
                  id: "agent.page.notFoundHint",
                  defaultMessage: "This AI agent does not exist, or was deleted.",
                })
          }
        />
      </main>
    );
  }

  return (
    <main className={MAIN} data-graphql="Agent">
      <div className="mb-5">
        <BackButton />
      </div>
      <div className="box bg-surface px-5 pb-5">
        <AgentDetail
          agent={agent}
          action={
            canEdit ? (
              <Link
                id="agent-page-edit"
                to={agentsAdminPath(agent.id)}
                className={buttonClasses({ variant: "ghost", size: "sm" })}
              >
                <FormattedMessage id="agent.page.edit" defaultMessage="Edit in master data" />
              </Link>
            ) : null
          }
        />
      </div>
    </main>
  );
}
